// The Report state creates two portions of cayenne params.

/**
 * If seasonFrom and seasonTo are the same we want to show a players team
 * So we will not pass in the agg flag
 *
 * The data will still be agg'd. We just will select individual columns in report.csv
 */
const isAggregatedReport = (report) => {
    if (report.timeFrame === 'season' && report.seasonFrom === report.seasonTo) {
        return false;
    }
    return report.isAggregate;
};

// genBaseReportParams represents parameters that are always needed for cayenne fetches
const genBaseReportCayenneParams = (appliedReport) => {
    const isAggregate = isAggregatedReport(appliedReport);
    const isGame = appliedReport.timeFrame === 'game';
    return `?isAggregate=${isAggregate}&isGame=${isGame}`;
};

// franchiseValue is a string (i.e. 'franchise.1')
const getFranchiseParam = (franchiseValue) => {
    const [grouping, value] = franchiseValue.split('.');
    const key = 'franchiseId';
    return `${key}=${value}`;
};

// For positions, there's a value to represent L/R/C so we need to catch that
const getPositionParam = (cayenneKey, cayenneVal) => {
    if (cayenneVal === 'F') {
        return `(${cayenneKey}="L" or ${cayenneKey}="R" or ${cayenneKey}="C")`;
    }
    return `${cayenneKey}="${cayenneVal}"`;
};

// This function gets the keys used to create the other part of the cayenne expression
const getKeysForCayenneExp = (reportState) => {
    const { timeFrame } = reportState;
    const baseKeysToExclude = ['isAggregate', 'timeFrame', 'reportName', 'isDateRangeFromSeasons'];
    const keysToExclude =
        timeFrame === 'game' && !reportState.isDateRangeFromSeasons
            ? [...baseKeysToExclude, 'seasonFrom', 'seasonTo']
            : [...baseKeysToExclude, 'dateFrom', 'dateTo'];

    return Object.keys(reportState).filter((key) => !keysToExclude.includes(key));
};

// Some cayenne params, like seasonFrom/To and dateFrom/To, need >=/<=
const getOperator = (key) => {
    if (key.includes('From')) {
        return '>=';
    }
    if (key.includes('To')) {
        return '<=';
    }
    return '=';
};

// formatValue takes in reportState and key to accomodate for the following edge case:
// If dateTo is equal to dateFrom, add an end-of-day timestamp to dateTo
const formatValue = (reportState, key) => {
    const value = reportState[key];

    if (key === 'dateTo') {
        return `"${value} 23:59:59"`;
    }

    if (Number.isNaN(Number(value))) {
        return `"${value}"`;
    }
    return value;
};

const cayenneKeysData = {
    dateFrom: 'gameDate',
    dateTo: 'gameDate',
    seasonFrom: 'seasonId',
    seasonTo: 'seasonId',
};

// Below represents parameters that may not always be present
// The resulting array is to be joined with another cayenne exp array (from filters) when fetching data
const genReportDetailsCayenneParams = (reportState) =>
    getKeysForCayenneExp(reportState)
        .filter((key) => reportState[key] && key !== 'players')
        .map((key) => {
            if (key === 'franchiseId') {
                return encodeURIComponent(getFranchiseParam(reportState[key]));
            }
            if (key === 'positionCode') {
                return encodeURIComponent(getPositionParam(key, reportState[key]));
            }

            const cayenneKey = cayenneKeysData[key] ? cayenneKeysData[key] : key;
            const operator = getOperator(key);
            const value = formatValue(reportState, key);

            return `${cayenneKey}${operator}${encodeURIComponent(value)}`;
        });

// Get any non-default values from the Report state and convert them into an array of query-parameters

const getAppliedReport = (state) => state.playersAndTeams.report.applied;
const getReportCategory = (state) => state.playersAndTeams.report.reportCategory;

export default {
    isAggregatedReport,
    genBaseReportCayenneParams,
    genReportDetailsCayenneParams,
    getAppliedReport,
    getReportCategory,
};
